import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button, Link } from '@acast-tech/decibel';
import { CircleExclamationDuotoneGlyph } from '@acast-tech/decibel-glyphs';
import { EmptyState, NotFound, useAppConfig } from '@acast-tech/product-suite-app-shell';
import type { Glyph } from '@acast-tech/decibel-glyphs';

interface ErrorStateProps {
  action?: React.ReactNode;
  intent?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  glyph?: Glyph;
  title?: string;
  desc?: string;
}

/**
 * React Router `errorElement` handler. Reads the thrown value via `useRouteError` and renders
 * the appropriate UI: `NotFound` for 404, or a generic fallback for all other errors.
 *
 * Mount this as `errorElement` on a `<Route>`.
 * It is not a React render error boundary and cannot catch errors thrown during component render.
 */
const RouterErrorBoundary = ({
  action = <ErrorStateAction />,
  intent,
  glyph,
  title,
  desc,
}: ErrorStateProps) => {
  const error = useRouteError();

  // Check if it's a route error response
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <NotFound />;
    }
  }
  return <FallbackError action={action} intent={intent} glyph={glyph} title={title} desc={desc} />;
};

const FallbackError = ({ action, intent, glyph, title, desc }: ErrorStateProps) => {
  const appConfig = useAppConfig();

  return (
    <EmptyState
      intent={intent ?? 'danger'}
      glyph={glyph ?? CircleExclamationDuotoneGlyph}
      title={title ?? 'Something went wrong'}
      desc={
        desc ?? (
          <>
            This may be because of a technical error that we&apos;re working to get fixed. Please
            try again or contact our{' '}
            <Link href={`mailto:${appConfig.supportEmail}`}>support team</Link>.
          </>
        )
      }
      action={action}
    />
  );
};

const ErrorStateAction = () => {
  return (
    <Button variant="primary" onClick={() => window.location.reload()}>
      Reload page
    </Button>
  );
};

export default RouterErrorBoundary;
